import {
  Appointment,
  QUEUE_CARD_ACTIONS,
  QUEUE_TYPE,
  SUBJECT_KEYS,
  VendorMetaItem,
  VendorMetaType,
} from "@elixir/types";
import { Dropdown, Menu, Tooltip } from "antd";
import moment from "dayjs";
import { useEffect, useMemo } from "react";
import { ReactComponent as EkaIcon } from "../../../assets/icons/custom/eka.svg";
import { ReactComponent as CaretDownRegularIcon } from "../../../assets/icons/font-awesome/caret-down-regular.svg";
import { ReactComponent as ChildrenRegularIcon } from "../../../assets/icons/font-awesome/children-regular.svg";
import { ReactComponent as CityRegularIcon } from "../../../assets/icons/font-awesome/city-regular.svg";
import { ReactComponent as ClockRotateLeftRegularIcon } from "../../../assets/icons/font-awesome/clock-rotate-left-regular.svg";
import { ReactComponent as EditRegularIcon } from "../../../assets/icons/font-awesome/edit-regular.svg";
import { ReactComponent as EnvelopRegularIcon } from "../../../assets/icons/font-awesome/envelop-regular.svg";
import { ReactComponent as HashtagRegularIcon } from "../../../assets/icons/font-awesome/hashtag-regular.svg";
import { ReactComponent as IndianRupeeSign } from "../../../assets/icons/font-awesome/indian-rupee-sign-regular.svg";
import UpdateAppointmentPaymentStatus from "../../button/queue/columns/update-appointment-payment-status";
import { ReactComponent as LocationDotRegularIcon } from "../../../assets/icons/font-awesome/location-dot-regular.svg";
import { ReactComponent as NotesRegularIcon } from "../../../assets/icons/font-awesome/notes-regular.svg";
import { ReactComponent as PhoneRegularIcon } from "../../../assets/icons/font-awesome/phone-regular.svg";
import { ReactComponent as RetweetRegularIcon } from "../../../assets/icons/font-awesome/retweet-regular.svg";
import { ReactComponent as TagRegularIcon } from "../../../assets/icons/font-awesome/tag-regular.svg";
import { ReactComponent as UserCircleRegularIcon } from "../../../assets/icons/font-awesome/user-circle-regular.svg";
import { ReactComponent as UserDoctorRegularIcon } from "../../../assets/icons/font-awesome/user-doctor-regular.svg";
import { ReactComponent as UserRegularIcon } from "../../../assets/icons/font-awesome/user-regular.svg";
import { ReactComponent as WalkingRegularIcon } from "../../../assets/icons/font-awesome/walking-regular.svg";
import useBillingStore from "../../../stores/billing-store/billing-store";
import { FormData } from "../../../stores/queue-store/types";
import { getPatientMainInfoString } from "../../../utils/appointment-utils";
import {
  getCreateApptConfig,
  navigate,
  onPatientEditAction,
} from "../../../utils/bridges/methods";
import { settleDues } from "../../../utils/receipt";
import { track } from "../../../utils/tracking";
import {
  ArrowUpRightFromSquareRegular,
  CircleInfoRegularIcon,
} from "@elixir/icons";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";
import format from "string-template";
import { getAclViewClassName } from "@elixir/utils";
import getB from "../../../utils/bridges/EkaBridge";
import useQueueStore from "../../../stores/queue-store";
import { useQuery } from "@tanstack/react-query";
import { CREATE_APPT_CONFIG_QUERY_KEY } from "../../../constants/query-keys";

const itemStyle =
  "dr-flex dr-items-center dr-justify-between BodyBody2Regular dr-py-2 dr-text-darwin-touch-neutral-1000 dr-space-x-16";
const valueStyle = "dr-text-wrap dr-text-right";

const UserData = ({
  apt,
  hideCaretDown = false,
  className = "",
  queueType,
}: {
  apt: Appointment;
  hideCaretDown?: boolean;
  className?: string;
  queueType?: QUEUE_TYPE;
}) => {
  const {
    aid,
    patient,
    formData = [],
    last_visit_time,
    visit_count,
    patient_oid,
    vendor_meta = [],
  } = apt || {};
  const { mobile } = patient || {};
  const uhidData = formData?.find((d) => d.key.toLowerCase() === "uhid");
  const { data: queueConfig } = useGetQueueConfig();
  const staticLinks = queueConfig?.data?.static_assets?.links || [];

  const rightPanelQueueType = useQueueStore(
    (state) => state.rightPanelQueueType
  );
  const { workflow_views } = queueConfig?.data || {};
  const isCustomQueue = useMemo(() => {
    const defaultViewIds = Object.values(QUEUE_TYPE) as string[];
    return !defaultViewIds.includes(rightPanelQueueType);
  }, [rightPanelQueueType]);

  const { data: createApptConfig } = useQuery({
    queryKey: [CREATE_APPT_CONFIG_QUERY_KEY],
    queryFn: getCreateApptConfig,
  });

  const isPaymentStatusTurnedOnForDefaultQueue = useMemo(() => {
    if (!createApptConfig) return false;
    const associatePaymentsConfig = createApptConfig.find(
      (c) => c.id === "associate_payments_to_appointments"
    );
    return !!associatePaymentsConfig?.isShown;
  }, [createApptConfig]);

  const doesPaymentStatusExistInCustomQueue = useMemo(() => {
    if (!workflow_views?.length) return false;
    const currentView = workflow_views.find(
      (view) => view.viewId === rightPanelQueueType
    );
    if (!currentView?.actions?.length) return false;
    return currentView.actions.some((row) =>
      row?.some(
        (action) =>
          action.colId === QUEUE_CARD_ACTIONS.UPDATE_APPOINTMENT_PAYMENT_STATUS
      )
    );
  }, [workflow_views, rightPanelQueueType]);

  const setClinicId = useBillingStore((state) => state.setClinicId);

  const showCaretIcon =
    (!!last_visit_time ||
      !!visit_count ||
      formData.length > 0 ||
      !!patient.amount_pending ||
      !!vendor_meta.length ||
      !!staticLinks.length) &&
    !hideCaretDown;

  let sortedMetaDataOFAppointment: VendorMetaItem[] = [];
  if (showCaretIcon && vendor_meta && vendor_meta.length > 0) {
    sortedMetaDataOFAppointment = (vendor_meta || [])?.sort((a, b) =>
      a?.name?.localeCompare(b?.name, undefined, { sensitivity: "base" })
    );
  }

  const menu = (
    <Menu className="dr-rounded-8 dr-overflow-hidden dr-w-290 dr-divide-y-1 dr-divide-doctor-darwin-neutral-100">
      {last_visit_time ? (
        <Menu.Item icon={<ClockRotateLeftRegularIcon />}>
          <div className={itemStyle}>
            <div>Last Visit</div>
            <div className={valueStyle}>
              {moment(last_visit_time as string).format("DD MMM YYYY")}
            </div>
          </div>
        </Menu.Item>
      ) : (
        <></>
      )}
      {visit_count ? (
        <Menu.Item icon={<WalkingRegularIcon />}>
          <div className={itemStyle}>
            <div>Total Visits</div>
            <div className={valueStyle}>{visit_count}</div>
          </div>
        </Menu.Item>
      ) : (
        <></>
      )}
      {formData.map((patient, idx) => {
        return (
          <Menu.Item
            key={`${patient.key}-${idx}`}
            icon={getFormDataIcon(patient.key)}
          >
            <div className={itemStyle}>
              <div>{patient.label}</div>
              <div className={valueStyle}>{patient.value}</div>
            </div>
          </Menu.Item>
        );
      })}
      {patient.amount_pending && patient.amount_pending > 0 ? (
        <Menu.Item
          onClick={() => {
            setClinicId({ clinicId: apt.clinic_id });
            // setReceiptPopupData({ aid: apt.aid, receiptState: "settle-due" });
            settleDues({ appointment: apt });
          }}
          icon={<IndianRupeeSign />}
        >
          <div className={itemStyle}>
            <div>Pending Dues</div>
            <div className={valueStyle}>
              ₹ {patient.amount_pending.toLocaleString("en-IN")}
            </div>
          </div>
        </Menu.Item>
      ) : null}
      {sortedMetaDataOFAppointment?.length > 0 &&
        sortedMetaDataOFAppointment?.map((item, idx) => {
          const Wrapper = item.type === VendorMetaType.LINK ? "a" : "div";

          return (
            <Menu.Item
              key={item.id}
              icon={
                item.type === VendorMetaType.LINK ? (
                  <ArrowUpRightFromSquareRegular />
                ) : (
                  <CircleInfoRegularIcon />
                )
              }
            >
              <Wrapper
                href={
                  item.type === VendorMetaType.LINK ? item.value : undefined
                }
                target={
                  item.type === VendorMetaType.LINK ? "_blank" : undefined
                }
                className={itemStyle}
              >
                <div className="dr-capitalize">{item.name}</div>
                <div className={valueStyle}>
                  {item.type === VendorMetaType.LINK ? "" : item.value}
                </div>
              </Wrapper>
            </Menu.Item>
          );
        })}
      {staticLinks.map((item, idx) => (
        <Menu.Item key={idx} icon={<ArrowUpRightFromSquareRegular />}>
          <a
            href={format(item.link, {
              patient_oid: patient.oid,
              patient_uhid: apt.formData?.find((item) => item.key === "uhid")
                ?.value,
              aid: apt.aid,
              clinic_partner_id: getB()
                ?.getMe()
                ?.clinics.find((t) => t.id === apt.clinic_id)
                ?.partner_clinic_id,
              user_partner_id: queueConfig?.data?.loginmeta?.partner_id,
            })}
            target={"_blank"}
            className={itemStyle}
            rel="noreferrer"
          >
            <div className="dr-capitalize">{item.title}</div>
            <div></div>
          </a>
        </Menu.Item>
      ))}
    </Menu>
  );
  const patientInfo = useMemo(() => {
    return getPatientMainInfoString({ patient, formData });
  }, [patient, formData]);

  const handlePatientNameClick = () => {
    track("patient_name_click", {
      queueType,
    });
    if (patient_oid) {
      navigate("push", `/app/patient/${patient_oid}/visit`);
    } else {
      alert("Something went wrong");
    }
  };

  const isOnApp = [1, 2, 6].includes(apt?.patient?.type!);
  const hasLabelContent = uhidData?.value?.trim() || mobile;

  return (
    <div className={`dr-flex dr-space-x-8 ${className}`}>
      <div className="dr-self-start dr-py-4">
        {isOnApp ? (
          <EkaIcon className="dr-min-w-13 dr-w-13" />
        ) : (
          <UserRegularIcon className="dr-min-w-13 dr-w-13 dr-text-darwin-touch-neutral-800" />
        )}
      </div>
      <div className="dr-flex dr-flex-col dr-space-y-4 dr-w-full">
        <div className="dr-flex dr-items-center dr-flex-1 dr-justify-between">
          <div className="dr-flex dr-items-center dr-space-x-8">
            <div
              onClick={handlePatientNameClick}
              className="TouchCalloutBold dr-text-darwin-touch-neutral-1000 group-hover:dr-text-darwin-touch-primary-primary hover:dr-underline dr-cursor-pointer"
            >
              <span className="">{patientInfo[0]}</span>
              <span>
                {patientInfo.slice(1).length > 0
                  ? ` | ${patientInfo.slice(1).join(" | ")}`
                  : ""}
              </span>
            </div>
            {patient_oid && <FamilyButtonIcon pid={patient_oid} aid={aid} />}
            {showCaretIcon ? (
              !!patient.amount_pending && patient.amount_pending > 0 ? (
                <Dropdown
                  trigger={["click"]}
                  overlay={menu}
                  placement="bottomLeft"
                >
                  <div className="dr-flex dr-items-center dr-space-x-4 dr-px-4 dr-py-2 dr-rounded-4 dr-bg-darwin-touch-red-bg-light dr-cursor-pointer">
                    <Tooltip
                      title={`Pending Dues: ₹ ${patient.amount_pending}`}
                    >
                      <div className="dr-p-4">
                        <IndianRupeeSign className="dr-text-darwin-touch-red-primary dr-w-12 dr-h-12" />
                      </div>
                    </Tooltip>
                    <div
                      onClick={(e) => {
                        track("caret_down", {
                          queueType,
                        });
                      }}
                      className="dr-m-4"
                    >
                      <CaretDownRegularIcon className="dr-w-12 dr-text-darwin-touch-neutral-600 dr-cursor-pointer" />
                    </div>
                  </div>
                </Dropdown>
              ) : (
                <div
                  className="dr-self-start dr-my-4 dr-w-16 dr-h-16 dr-flex dr-items-center dr-justify-center"
                  onClick={(e) => {
                    track("caret_down", {
                      queueType,
                    });
                    e.stopPropagation();
                  }}
                >
                  <Dropdown
                    trigger={["click"]}
                    overlay={menu}
                    placement="bottomLeft"
                  >
                    <div className="dr-w-24 dr-h-24 dr-min-w-24 dr-flex dr-items-center dr-justify-center">
                      <CaretDownRegularIcon className="dr-w-12 dr-text-darwin-touch-neutral-600 dr-cursor-pointer" />
                    </div>
                  </Dropdown>
                </div>
              )
            ) : (
              <></>
            )}
          </div>
          <EditRegularIcon
            onClick={() => {
              onPatientEditAction(apt);
            }}
            className={`dr-w-24 dr-self-start dr-h-24 dr-min-w-24 dr-p-4 dr-cursor-pointer hover:dr-text-darwin-touch-primary-primary dr-text-darwin-touch-neutral-600 dr-invisible group-hover:dr-visible ${getAclViewClassName(SUBJECT_KEYS.PATIENT_DATABASE)}`}
          />
        </div>
        <div className="dr-flex dr-items-center dr-space-x-8">
          {hasLabelContent && (
            <div className="TouchLabelRegular dr-text-darwin-touch-neutral-600 dr-space-x-3">
              {uhidData?.value?.trim() && <span>#{uhidData?.value}</span>}
              {uhidData?.value?.trim() && mobile && <span>/</span>}
              {mobile && (
                <span
                  className={getAclViewClassName(
                    SUBJECT_KEYS.PATIENT_MOBILE_NUMBER
                  )}
                >
                  {mobile}
                </span>
              )}
            </div>
          )}
          {renderPaymentStatus(
            isCustomQueue,
            apt,
            doesPaymentStatusExistInCustomQueue,
            isPaymentStatusTurnedOnForDefaultQueue
          )}
        </div>
      </div>
    </div>
  );
};

// if default queue > render is controlled by advance settings modal (associate payments to appointments).
// if custom queue > render is controlled by whether the column id exist in queue card config or not
const renderPaymentStatus = (
  isCustomQueue: boolean,
  appointment: Appointment,
  doesColumnIdExistInCustomQueue: boolean,
  isPaymentStatusTurnedOnForDefaultQueue: boolean
) => {
  if (!isCustomQueue && isPaymentStatusTurnedOnForDefaultQueue) {
    return <UpdateAppointmentPaymentStatus apt={appointment} />;
  }

  if (isCustomQueue && doesColumnIdExistInCustomQueue) {
    return <UpdateAppointmentPaymentStatus apt={appointment} />;
  }

  return null;
};

export default UserData;

const getFormDataIcon = (key: FormData["key"]) => {
  switch (key) {
    case "uhid":
      return <HashtagRegularIcon />;
    case "tag":
      return <TagRegularIcon />;
    case "paddress":
      return <UserCircleRegularIcon />;
    case "pincode":
      return <LocationDotRegularIcon />;
    case "email":
      return <EnvelopRegularIcon />;
    case "bloodgroup":
      return <UserCircleRegularIcon />;
    case "salutation":
      return <UserCircleRegularIcon />;
    case "city":
      return <CityRegularIcon />;
    case "aphone":
      return <PhoneRegularIcon />;
    case "referredby":
      return <RetweetRegularIcon />;
    case "referredbynumber":
      return <PhoneRegularIcon />;
    case "maritalstatus":
      return <ChildrenRegularIcon />;
    case "noi":
      return <NotesRegularIcon />;
    case "channel":
      return <NotesRegularIcon />;
    case "pocc":
      return <UserDoctorRegularIcon />;
    default:
      return <NotesRegularIcon />;
  }
};

export const FamilyButtonIcon: React.FC<{
  pid: string;
  aid: string;
}> = ({ pid, aid }) => {
  useEffect(() => {
    if (window.renderFamilyIcon) {
      window.renderFamilyIcon({
        containerId: `family-button-${aid}`,
        pid,
      });
    }
  }, []);
  return <div id={`family-button-${aid}`}></div>;
};
